import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Visuals';
import webPackage from '../../package.json';

export const NAV = ['Overview','Opportunity Radar','Mission Control','Prospect Finder','Email Center','Contacts','Performance','Settings'] as const;
export type PageName = typeof NAV[number] | 'Companies';

const NAV_ICONS:Record<(typeof NAV)[number],string>={
  'Overview':'dashboard',
  'Opportunity Radar':'radar',
  'Mission Control':'target',
  'Prospect Finder':'person_search',
  'Email Center':'mail',
  'Contacts':'contacts',
  'Performance':'monitoring',
  'Settings':'settings'
};

export function Shell({page,onPageChange,connection,children,notices=[],onDismissNotice,tools}:{page:PageName;onPageChange:(p:PageName)=>void;connection:string;children:ReactNode;notices?:Array<{id:string;message:string;tone:'info'|'error'}>;onDismissNotice?:(id:string)=>void;tools?:ReactNode}){
  const [tabletMenuOpen,setTabletMenuOpen]=useState(false);
  const menuButtonRef=useRef<HTMLButtonElement>(null);
  const navRef=useRef<HTMLElement>(null);
  const dismissTabletMenu=(restoreFocus=false)=>{
    setTabletMenuOpen(false);
    if(restoreFocus)window.requestAnimationFrame(()=>menuButtonRef.current?.focus());
  };
  useEffect(()=>{
    if(!tabletMenuOpen)return;
    const onKeyDown=(event:KeyboardEvent)=>{
      if(event.key==='Escape'){event.preventDefault();dismissTabletMenu(true);}
      if(event.key==='Tab'&&navRef.current){
        const controls=[...navRef.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
        if(!controls.length)return;
        const first=controls[0],last=controls[controls.length-1];
        if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}
        else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
      }
    };
    document.addEventListener('keydown',onKeyDown);
    const previousOverflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    window.requestAnimationFrame(()=>navRef.current?.querySelector<HTMLElement>('button.active')?.focus());
    const onResize=()=>{if(window.innerWidth>1180||window.innerWidth<=760)setTabletMenuOpen(false);};
    window.addEventListener('resize',onResize);
    return ()=>{document.removeEventListener('keydown',onKeyDown);window.removeEventListener('resize',onResize);document.body.style.overflow=previousOverflow;};
  },[tabletMenuOpen]);
  useEffect(()=>{setTabletMenuOpen(false);},[page]);
  return <div className="appShell">
    {tabletMenuOpen&&<button type="button" className="tabletNavBackdrop" aria-label="Close navigation" onClick={()=>dismissTabletMenu(true)}/>}
    <aside className={`rail ${tabletMenuOpen?'tabletNavOpen':''}`} id="app-primary-navigation" aria-label="Main navigation" ref={navRef}>
      <button type="button" className="tabletNavClose" aria-label="Close navigation" onClick={()=>dismissTabletMenu(true)}><Icon name="close"/></button>
      <div className="productMark"><strong>CLIENT</strong><strong>OUTREACH</strong><span>TOOL</span></div>
      <nav>{NAV.map((name,i)=><button key={name} title={name} aria-label={name} className={name===page||(name==='Contacts'&&page==='Companies')?'active':''} onClick={()=>{dismissTabletMenu();onPageChange(name);}}><i>{String(i+1).padStart(2,'0')}</i><span className="navLabel"><Icon name={NAV_ICONS[name]}/>{name}</span></button>)}</nav>
      <div className="companyMark" aria-label="BAILEY Creative Studio"><img src="/bailey-wordmark.svg" alt="BAILEY"/><span>CREATIVE STUDIO</span></div>
    </aside>
    <main className="mainArea">
      <header className="topbar"><div className="topbarIdentity"><button ref={menuButtonRef} type="button" className="tabletNavToggle" aria-label="Open navigation" aria-expanded={tabletMenuOpen} aria-controls="app-primary-navigation" onClick={()=>setTabletMenuOpen(true)}><Icon name="menu"/><span>MENU</span></button><span className="topbarPage">{page==='Companies'?'Contacts':page}</span></div><div className="topbarTools">{tools}<div className="connection"><span className="statusDot"/><span className="connectionLabel">{connection}</span><span className="versionTag">v{webPackage.version}</span></div></div></header>
      {!!notices.length&&<div className="appNoticeStack" aria-live="polite">{notices.map(notice=><div key={notice.id} className={`appNotice ${notice.tone==='error'?'error':''}`} role={notice.tone==='error'?'alert':'status'}><span>{notice.tone==='error'?<><strong>ACTION NOT COMPLETED</strong><em>{notice.message}</em></>:notice.message}</span><button onClick={()=>onDismissNotice?.(notice.id)} aria-label="Dismiss message"><Icon name="close"/></button></div>)}</div>}
      <div className="pageMotion" key={page}>{children}</div>
    </main>
  </div>;
}

export function WorkspaceTitle({title,copy,actions}:{title:ReactNode;copy:string;actions?:ReactNode}){
  return <div className="workspaceTitle"><div><h1>{title}</h1></div><div className="workspaceCopy"><p>{copy}</p>{actions}</div></div>;
}

export function TextButton({children,onClick,disabled=false}:{children:ReactNode;onClick?:()=>void;disabled?:boolean}){
  return <button className="textBtn" onClick={onClick} disabled={disabled}>{children}</button>;
}
